import {
  type LucideIcon,
  BookOpen,
  BriefcaseMedical,
  ClipboardList,
  FileCheck,
  FileText,
  HeartPulse,
  LifeBuoy,
  ListChecks,
  Microscope,
  NotebookPen,
  Send,
  Settings,
  Stethoscope,
  Target,
  UserRoundSearch,
  Users,
} from 'lucide-react';

import { Button } from '@heidi/design-system/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { TypographyH2, TypographyP } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { type TemplateResponse } from '@heidi/templates/contracts';

import { onboardingToggleButtonClassName } from '../shared';

const SKELETON_COUNT = 8;

const KEYWORD_ICONS: [string[], LucideIcon][] = [
  [['referral'], Send],
  [['explainer', 'patient', 'letter'], Users],
  [['form', 'cover'], Settings],
  [['advice', 'guidance', 'counsel'], ListChecks],
  [['clinician', 'referring', 'response'], UserRoundSearch],
  [['smart', 'goal', 'target'], Target],
  [['care', 'plan'], LifeBuoy],
  [['soap'], ClipboardList],
  [['consult', 'exam', 'assessment'], Stethoscope],
  [['implant', 'surg', 'procedure'], BriefcaseMedical],
  [['review', 'follow'], FileCheck],
  [['history', 'record', 'h & p', 'h&p'], BookOpen],
  [['lab', 'test', 'investigation'], Microscope],
  [['vital', 'monitor', 'pulse'], HeartPulse],
  [['note', 'summary'], NotebookPen],
  [['issue', 'list', 'problem'], ListChecks],
];

const FALLBACK_ICONS: LucideIcon[] = [
  FileText,
  ClipboardList,
  Stethoscope,
  BookOpen,
  NotebookPen,
  BriefcaseMedical,
  HeartPulse,
  Microscope,
  FileCheck,
  Target,
];

function getTemplateIcon(name: string, index: number): LucideIcon {
  const lowerName = name.toLowerCase();
  for (const [keywords, icon] of KEYWORD_ICONS) {
    if (keywords.some((kw) => lowerName.includes(kw))) return icon;
  }
  return FALLBACK_ICONS[index % FALLBACK_ICONS.length]!;
}

interface DocumentSelectionStepProps {
  templates: TemplateResponse[];
  selectedIds: Set<string>;
  onToggle: (templateId: string) => void;
  onSkip: () => void | Promise<void>;
  onContinue: () => void | Promise<void>;
  isLoading: boolean;
  isSubmitting?: boolean;
}

export function DocumentSelectionStep({
  templates,
  selectedIds,
  onToggle,
  onSkip,
  onContinue,
  isLoading,
  isSubmitting = false,
}: DocumentSelectionStepProps) {
  const { t } = useTranslations();

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <div className="flex shrink-0 flex-col gap-4">
        <TypographyH2>{t('signup.documentSelectionTitle')}</TypographyH2>
        <TypographyP className="text-text-primary">
          {t('signup.documentSelectionSubtitle')}
        </TypographyP>
      </div>

      <div className="-mr-2 flex min-h-0 flex-1 flex-wrap content-start gap-3 overflow-y-auto pr-2">
        {isLoading
          ? Array.from({ length: SKELETON_COUNT }).map((_, i) => (
              <div
                key={i}
                className="h-10 w-36 animate-pulse rounded-lg border border-border bg-surface"
              />
            ))
          : templates.map((template, index) => {
              const Icon = getTemplateIcon(template.name, index);
              const isSelected = selectedIds.has(template.id);

              return (
                <Tooltip key={template.id}>
                  <TooltipTrigger asChild>
                    <Button
                      size="lg"
                      type="button"
                      variant="outline"
                      onClick={() => onToggle(template.id)}
                      className={onboardingToggleButtonClassName(
                        isSelected,
                        'max-w-[400px] gap-2'
                      )}
                    >
                      <Icon size={16} className="shrink-0" />
                      <span className="min-w-0 truncate">{template.name}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{template.name}</TooltipContent>
                </Tooltip>
              );
            })}
      </div>

      <div className="flex w-full shrink-0 flex-col gap-2">
        <Button
          size="lg"
          type="button"
          onClick={() => void onContinue()}
          disabled={isLoading || isSubmitting || selectedIds.size === 0}
          className="w-full"
        >
          {t('signup.continue')}
        </Button>
        <Button
          size="lg"
          type="button"
          variant="ghost"
          onClick={() => void onSkip()}
          disabled={isSubmitting}
          className="w-full"
        >
          {t('signup.skip')}
        </Button>
      </div>
    </div>
  );
}
